import { memo, useMemo } from 'react'
import { ButtonBase, IconButton, SxProps } from '@mui/material'
import { ChevronLeftIcon, ChevronRightIcon } from '@/assets'
import { formatDate } from '@/utils'
import { Text } from '@/components/Data'
import { Box } from '@/components/Layout'
import { dateRangePickerHeaderStyles as styles } from './DateRangePickerHeader.style'
import { DateRangePickerHeaderProps } from './DateRangePickerHeader.types'

function DateRangePickerHeader({
  currentMonth,
  onPrevMonth,
  onNextMonth,
  onHeaderClick,
  disablePastDates,
  disableFutureDates,
  minDate,
  maxDate,
  sx,
  previousButtonProps = {},
  nextButtonProps = {},
  headerButtonProps = {},
  textProps = {},
  ...rest
}: DateRangePickerHeaderProps) {
  const isPrevDisabled = useMemo(() => {
    let disabled = false

    if (disablePastDates) disabled = currentMonth.isSameOrBefore(new Date(), 'month')
    else if (minDate) disabled = currentMonth.isSameOrBefore(minDate, 'month')

    return disabled
  }, [currentMonth, disablePastDates, minDate])

  const isNextDisabled = useMemo(() => {
    let disabled = false

    if (disableFutureDates) disabled = currentMonth.isSameOrAfter(new Date(), 'month')
    else if (maxDate) disabled = currentMonth.isSameOrAfter(maxDate, 'month')
    return disabled
  }, [currentMonth, disableFutureDates, maxDate])

  // props extractions
  const { sx: previousButtonSx, ...otherPreviousButtonProps } = previousButtonProps
  const { sx: nextButtonSx, ...otherNextButtonProps } = nextButtonProps
  const { sx: textSx, ...otherTextProps } = textProps

  return (
    <Box sx={[styles.wrapper, sx] as SxProps} {...rest}>
      <IconButton
        sx={[styles.button, previousButtonSx] as SxProps}
        onClick={onPrevMonth}
        disabled={isPrevDisabled}
        {...otherPreviousButtonProps}>
        <ChevronLeftIcon className="icon" />
      </IconButton>

      <ButtonBase onClick={onHeaderClick} {...headerButtonProps}>
        <Text sx={[styles.monthText, textSx] as SxProps} {...otherTextProps}>
          {formatDate(currentMonth, 'MMMM YYYY')}
        </Text>
      </ButtonBase>

      <IconButton
        sx={[styles.button, nextButtonSx] as SxProps}
        onClick={onNextMonth}
        disabled={isNextDisabled}
        {...otherNextButtonProps}>
        <ChevronRightIcon className="icon" />
      </IconButton>
    </Box>
  )
}

export default memo(DateRangePickerHeader)
